import { useEffect, useRef, useState } from 'react';

import { inventoryLotsByLotIdApi } from 'api/inventory';
import {
  getLotEntriesForNdc,
  getLotNdcMismatch,
  isValidLotNumber,
} from 'modules/StockManagement/utils/stockLineValidation';

import { isSameLotNumber } from '../utils/bulkManualLotEntry';

/** Long enough that a lot number is not looked up on every keystroke. */
const LOT_LOOKUP_DEBOUNCE_MS = 400;

const IDLE = { lotNumber: '', entries: [], loading: false, failed: false };

/**
 * POM-6121 — is this lot number one inventory already holds under a DIFFERENT NDC?
 *
 * The one place the bulk picker talks to the network, and the reason it has to: the answer cannot
 * come from the batch payload, which carries only this product's own NDCs, so a number belonging
 * to another product is invisible there. One debounced lookup per typed value rather than one per
 * row, so "edit the batch against a single snapshot" still holds for everything else.
 *
 * Same check, same API and same wording as the single-item Fill Product panel and receiving.
 *
 * `cache` is the sitting's answers, owned by the review dialog. One vial commonly covers several
 * rows of a batch, so the same number is typed again and again; asking twice is the call worth not
 * making. A stale answer is not a risk worth guarding: it would take someone creating that lot
 * elsewhere mid-sitting, and the commit re-validates server-side.
 */
export const useForeignLotLookup = ({ enabled, lotNumber, ndc, cache }) => {
  const [lookup, setLookup] = useState(IDLE);
  const sequence = useRef(0);

  const typedLot = String(lotNumber ?? '').trim();
  /**
   * Is there a question to ask at all?
   *
   * A number the format rule already refuses is not worth a request — the panel reports the format
   * first, so the answer would be discarded whatever it said. Decided once and read by both the
   * effect and the result below, so a value that is never looked up cannot be reported as a lookup
   * still running: that would hold the caller's button for an answer that is never coming.
   */
  const shouldCheck = enabled && !!typedLot && isValidLotNumber(typedLot);

  useEffect(() => {
    /**
     * Bumped FIRST, before either early return, because it marks "everything outstanding is now
     * stale" rather than "I am about to fire". Bumping it only on the path that fires left a
     * request in flight able to overwrite a later cache hit: type LOT12, backspace to LOT1 while
     * it is in flight, the cache answers LOT1 instantly, then LOT12's response lands and still
     * passes the guard. The reported lot number no longer matches the typed one, so the panel
     * reads as forever pending — Add lot spinning, Apply dead, and no message saying why.
     */
    const seq = sequence.current + 1;
    sequence.current = seq;

    if (!shouldCheck) {
      setLookup(IDLE);

      return undefined;
    }

    const cacheKey = typedLot.toLowerCase();
    const cached = cache?.get(cacheKey);
    if (cached) {
      setLookup({ lotNumber: typedLot, entries: cached, loading: false, failed: false });

      return undefined;
    }

    setLookup((prev) =>
      prev.lotNumber === typedLot
        ? prev
        : { lotNumber: typedLot, entries: [], loading: true, failed: false }
    );

    const timer = setTimeout(() => {
      inventoryLotsByLotIdApi({ lotId: typedLot })
        .then((entries) => {
          cache?.set(cacheKey, entries ?? []);
          if (sequence.current !== seq) return;
          setLookup({
            lotNumber: typedLot,
            entries: entries ?? [],
            loading: false,
            failed: false,
          });
        })
        .catch(() => {
          if (sequence.current !== seq) return;
          /* Blocking, not ignored. Nothing on the server refuses a lot number that belongs to
             another NDC — core resolves within this NDC's own inventory item, so an unmatched
             number simply creates a fresh lot here. Treating a failed check as "nothing found"
             would let through exactly the claim this check exists to stop. */
          setLookup({ lotNumber: typedLot, entries: [], loading: false, failed: true });
        });
    }, LOT_LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCheck, typedLot]);

  // Only trusted while it answers for the value currently typed.
  const isCurrent = isSameLotNumber(lookup.lotNumber, lotNumber);

  const answered = shouldCheck && isCurrent;

  return {
    foreignNdcLots: answered ? getLotNdcMismatch({ entries: lookup.entries, ndc }) : [],
    /* The rows for THIS NDC, which the batch snapshot cannot supply: it is scoped to the fill's
       own facility, and the same lot number legitimately sits at two sites. The caller decides
       what to do with them - here, whether the lot is already expired on file. */
    sameNdcLotsOnFile: answered ? getLotEntriesForNdc({ entries: lookup.entries, ndc }) : [],
    lookupFailed: answered && lookup.failed,
    /* Pending is not a problem, so callers hold the button on it rather than showing it as an
       error: a warning for an unfinished check reads as something being wrong. */
    isPending: shouldCheck && (!isCurrent || lookup.loading),
  };
};

export default useForeignLotLookup;
